import { useState, useEffect, useRef, useMemo } from "react";
import {
  usePayment,
  usePaymentMethods,
  setPaymentError,
  useTicket,
  useConfig,
  setSurchargeData,
  setShowSurchargeConfirmationModal,
  useSurcharge,
  setCardData,
} from "../../../store/Store";
import { processCloud9Payment } from "../utils/paymentHelpers";
import { saveCloud9PaymentMethod } from "../utils/paymentMethodsHelpers";
import { useTranslation } from "react-i18next";
import ApiWrapper from "../../general/ApiWrapper";
import {
  buildCardDetailsForCloud9FromTokenData,
  cloud9Funding,
  getBalanceDue,
} from "../../general/AppHelpers";
import {
  addSurchargeToTicketAdjustments,
  calculateSurcharge,
  isSurchargeApplicable,
  removeSurchargeFromTicketAdjustments,
} from "../../surcharge/api";
import SurchargeConfirmationModal from "../../surcharge/modal";

// component handles either adding a payment method OR processing a payment, depending on the addPaymentMethod prop
const Cloud9PaymentForm = ({ addPaymentMethod = false }) => {
  const { t } = useTranslation();

  const { isProcessing: paymentIsProcessing, success: paymentSuccess } =
    usePayment();
  const { selectedPaymentMethod } = usePaymentMethods();
  const { data: ticketData } = useTicket();
  const { accessToken } = useConfig();
  const { showSurchargeConfirmationModal } = useSurcharge();
  const [cloud9TokenData, setCloud9TokenData] = useState(null);

  const [iframeUrl, setIframeUrl] = useState(null);
  // const [seedData, setSeedData] = useState(null);

  // Use a ref to track if we've already fetched the seed data
  const hasFetchedSeedData = useRef(false);

  useEffect(() => {
    const fetchSeedData = async () => {
      // Skip if we don't have the necessary data or if we've already fetched
      if (
        !ticketData?.resource?.payment_account?.id ||
        hasFetchedSeedData.current
      )
        return;

      // Mark that we're fetching to prevent duplicate calls
      hasFetchedSeedData.current = true;

      try {
        const paymentAccountId = ticketData.resource.payment_account.id;
        const response = await ApiWrapper.post(
          `/stripe_accounts/${paymentAccountId}/webseed`,
          {},
          { accessToken }
        );

        // setSeedData(response.data);

        // Construct iframe URL
        const vtermBaseUrl =
          ticketData.resource.payment_account.vterm_url ||
          "https://testvterm.c9pg.com/CreateCardToken";
        const url = new URL(vtermBaseUrl);
        url.searchParams.append("Template", "MyTime_BW_Checkout");
        url.searchParams.append("WebGMID", response.data?.merchant_id);
        url.searchParams.append("SeedTime", response.data.timestamp);
        url.searchParams.append("Seed", response.data.seed);
        url.searchParams.append("GTID", response.data.terminal_id);
        url.searchParams.append("KeyIndex", response.data.key_index);
        url.searchParams.append("CallbackVersion", "2.0");
        url.searchParams.append("Version", "2.0");

        setIframeUrl(url.toString());
      } catch (err) {
        // Reset the flag if there was an error, so we can try again
        hasFetchedSeedData.current = false;

        setPaymentError({
          message:
            err.response?.data?.error ||
            err.message ||
            t("payment.initialize_fail"),
        });
      }
    };

    // Only fetch if we have ticket data and haven't fetched yet
    if (ticketData?.resource && !hasFetchedSeedData.current) {
      fetchSeedData();
    }

    // Cleanup function to reset the flag if the component unmounts
    return () => {
      // No cleanup needed
    };
  }, [ticketData, accessToken, t]);

  // Handle message from iframe
  useEffect(() => {
    const handleMessage = async (event) => {
      // Verify the origin of the message
      const vtermUrl =
        ticketData?.resource?.payment_account?.vterm_url ||
        "https://testvterm.c9pg.com";
      const vtermOrigin = new URL(vtermUrl).origin;

      // Skip origin check in development for testing
      if (
        process.env.NODE_ENV !== "development" &&
        event.origin !== vtermOrigin
      )
        return;

      try {
        // The message can be either a string (JSON) or an object
        let data = event.data;

        // If it's a string, try to parse it as JSON
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch (e) {
            console.warn("Could not parse message as JSON:", e);
            return; // Skip processing if we can't parse the message
          }
        }

        // Skip empty or irrelevant messages
        if (!data || typeof data !== "object") return;

        // Check if we have a successful response with a card token
        if (data.Status === "success" && data.CardToken) {
          if (addPaymentMethod) {
            // We're in the "Add New Payment Method" flow, so just save the card
            saveCloud9PaymentMethod({
              token: data.CardToken,
              tokenData: data,
              ticketData,
              accessToken,
            });
          } else {
            handlePaymentSuccess(data.CardToken, data);
          }
        } else if (
          data.Status === "error" ||
          (data.ErrorCode && data.ErrorCode !== "000")
        ) {
          setPaymentError({
            message:
              data.ErrorText ||
              data.ResultText ||
              t("payment.processing_error"),
          });
        }
      } catch (error) {
        console.error("Error processing message from payment form:", error);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketData, accessToken]);

  // checks if surcharge applicable
  const isSurchargeApplicableMemoized = useMemo(
    () => isSurchargeApplicable(ticketData),
    [ticketData]
  );

  const handleCancelSurchargeModal = async () => {
    setShowSurchargeConfirmationModal(false);
    removeSurchargeFromTicketAdjustments(ticketData);
    setSurchargeData(null);
    hasFetchedSeedData.current = false;
  };

  const handlePaymentSuccess = async (token, tokenData) => {
    const fundingType = tokenData
      ? cloud9Funding(tokenData?.CardDebitCreditIndicator)
      : selectedPaymentMethod?.card?.funding || "credit";
    const paymentAmount = getBalanceDue(ticketData);

    // Store token info early for later use
    const tokenPayload = { token, tokenData };
    setCloud9TokenData(tokenPayload);

    if (!isSurchargeApplicableMemoized || fundingType === "debit") {
      return processPayment(paymentAmount, fundingType, tokenPayload);
    }

    const { surcharge } = await calculateSurcharge(
      accessToken,
      fundingType,
      null,
      paymentAmount
    );
    if (!surcharge.successful) {
      setPaymentError({ message: t("payment.processing_error") });
      return;
    }

    // surcharging is enabled but there is no configuration
    // so, we will do regular payment
    if (!surcharge.surcharge_configuration_item) {
      processPayment(paymentAmount, fundingType, tokenPayload);
      return;
    }

    addSurchargeToTicketAdjustments(ticketData, surcharge);
    setSurchargeData(surcharge);
    setCardData(buildCardDetailsForCloud9FromTokenData(tokenData));
    setShowSurchargeConfirmationModal(true);
  };

  const proceedToPayment = (event) => {
    event.preventDefault();

    const fundingType = cloud9Funding(
      cloud9TokenData?.tokenData?.CardDebitCreditIndicator
    );
    const paymentAmount = getBalanceDue(ticketData);

    processPayment(paymentAmount, fundingType, cloud9TokenData);
  };

  // 🔁 Shared utility
  const processPayment = (amount, fundingType, tokenPayload) => {
    processCloud9Payment({
      accessToken,
      amount,
      paymentMethod: selectedPaymentMethod,
      paymentToken: tokenPayload?.token,
      card_type: fundingType,
      ticketData,
    });
  };

  if (paymentSuccess) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-4 mt-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-green-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              {t("payment.success")}
            </h3>
            <p className="text-sm text-green-700 mt-2">
              {t("payment.sucess_confirmation")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 border border-gray-200 rounded-md p-4 bg-gray-50">
      {selectedPaymentMethod ? (
        <div className="mb-4">
          <div className="mb-4">
            <label className="block text-black text-sm font-bold mb-2">
              {t("payment_methods.selected_payment_method")}
            </label>
            <div className="border rounded-md p-3 bg-white flex items-center">
              <span className="mr-2">💳</span>
              <span>
                •••• {selectedPaymentMethod.card?.last4 || "****"}
                {selectedPaymentMethod.card?.exp_month &&
                  selectedPaymentMethod.card?.exp_year && (
                    <span className="text-black-light text-sm ml-2">
                      {t("payment_methods.expires_on")}{" "}
                      {selectedPaymentMethod.card.exp_month}/
                      {selectedPaymentMethod.card.exp_year.toString().slice(-2)}
                    </span>
                  )}
              </span>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="ml-auto text-sm text-blue-500 hover:text-blue-700"
              >
                Change
              </button>
            </div>
          </div>
          <button
            onClick={() => handlePaymentSuccess()}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Pay Now
          </button>
        </div>
      ) : paymentIsProcessing ? (
        <div className="flex justify-center items-center py-8">
          <svg
            className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span>{t("payment.loading_payment_form")}.</span>
        </div>
      ) : iframeUrl ? (
        <div>
          <div className="mb-4 -mx-2">
            <iframe
              src={iframeUrl}
              title="Cloud9 Payment Form"
              width="100%"
              height="310"
              frameBorder="0"
              scrolling="no"
              allowtransparency="true"
              style={{ backgroundColor: "transparent" }}
              allow="payment"
            ></iframe>
          </div>

          {/* No checkboxes needed - behavior is determined by showAddPaymentForm */}
        </div>
      ) : (
        <div className="text-center py-4 text-black-light">
          {t("payment.load_payment_form_fail")}
        </div>
      )}

      <div className="mt-4 text-xs text-black-light text-center">
        {t("payment.securely_processed")}
      </div>

      {showSurchargeConfirmationModal && (
        <SurchargeConfirmationModal
          onConfirm={proceedToPayment}
          onClose={handleCancelSurchargeModal}
        />
      )}
    </div>
  );
};

export default Cloud9PaymentForm;
